import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Grid, Stack, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Select } from "@/components/elements/Select";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { TJobForm } from "@/features/job/types/JobForm";
import { useEnum } from "@/api/services/global/enum";
import { JOB_GENDER_OPTIONS } from "@/features/job/constants/enum";
import { DropZone } from "@/components/elements/DropZone";
import { TFileRules } from "@/utils/file";
import { TExtendFile } from "@/types/file";
import { SelectMulti } from "@/components/elements/SelectMulti";
import { useGetNationalityList, useGetSpecificSkillCertificateList } from "@/api/services/global/master";
import { JOB_DOCUMENT_FILE_MINE_TYPES, JOB_DOCUMENT_FILE_TYPES_EXTENSION, MAX_JOB_DOCUMENT_FILE, MAX_SIZE } from "@/features/job/constants/file";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { Textarea } from "@/components/elements/Textarea";
import { text } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { Modal } from "@/components/elements/Modal";
import { NATIONALITY_ANY_OPTION } from "@/constants/nationality";
import { JobSelectMulti } from "@/features/job/components/JobSelectMulti";
import { useDialog } from "@/hooks/useDialog";

type JobRequirementProps = {
  isDraft?: boolean;
  companySpecificIndustryIdWatchField?: number | null;
};

export const JobRequirement: FC<JobRequirementProps> = ({ isDraft, companySpecificIndustryIdWatchField }) => {
  const methods = useFormContext<TJobForm>();
  const { control, setValue } = methods;
  const { data: japaneseLevelTypeOptions } = useEnum("EJapaneseLevelType");
  const { data: recruitmentLocationTypeOptions } = useEnum("ERecruitmentLocationType");
  const { data: jobResidenceStatusOptions } = useEnum("EJobResidenceStatus");
  const { getCertificatesByIndustryId } = useGetSpecificSkillCertificateList();
  const { nationalityOptions, isLoading: isLoadingNationality } = useGetNationalityList();
  const rules: TFileRules = { acceptMimeTypes: JOB_DOCUMENT_FILE_MINE_TYPES, acceptFileTypesExtension: JOB_DOCUMENT_FILE_TYPES_EXTENSION };
  const confirmDeleteDialog = useDialog();
  const pendingDeleteFileName = useRef("");

  const nationalityOptionsWithAny = useMemo(() => [NATIONALITY_ANY_OPTION, ...nationalityOptions], [nationalityOptions]);

  const displayJobDocumentFiles = useWatch({ control, name: "displayJobDocumentFiles" }) || [];
  const jobDocumentFilePaths = useWatch({ control, name: "jobDocumentFilePaths" }) || [];
  const specificSkillCertificateIdsWatchField = useWatch({
    control,
    name: "specificSkillCertificateIds",
  });

  useEffect(() => {
    if (jobDocumentFilePaths.length > 0 && displayJobDocumentFiles.length === 0) {
      const displayFiles: TExtendFile[] = jobDocumentFilePaths.map(
        (filePath, index) =>
          ({
            id: index,
            name: filePath.split("/").pop() || "",
            filePath: filePath,
            type: filePath.split(".").pop() || "",
          }) as unknown as TExtendFile,
      );

      setValue("displayJobDocumentFiles", displayFiles);

      const existedPaths: string[] = displayFiles
        .filter((file) => "filePath" in file && file.filePath)
        .map((file) => (file as { filePath: string }).filePath as string);
      setValue("jobDocumentFilePaths", existedPaths);
    }
  }, [jobDocumentFilePaths, displayJobDocumentFiles.length, setValue, methods]);

  const handleChangeFile = (value: TExtendFile[] | TExtendFile | null) => {
    if (value === null) {
      setValue("displayJobDocumentFiles", []);
      setValue("jobDocumentFiles", []);
      setValue("jobDocumentFilePaths", []);
      return;
    }

    const newFiles = Array.isArray(value) ? value : [value];
    const validFiles = newFiles.filter(Boolean) as TExtendFile[];
    setValue("displayJobDocumentFiles", validFiles);

    const uploadFiles: TExtendFile[] = validFiles.filter((file) => file instanceof File) as TExtendFile[];
    setValue("jobDocumentFiles", uploadFiles);

    const existedPaths: string[] = validFiles
      .filter((file) => "filePath" in file && file.filePath)
      .map((file) => (file as { filePath: string }).filePath as string);

    setValue("jobDocumentFilePaths", existedPaths);
  };

  const handleFileRemove = (removedIndex: number, updatedFiles: TExtendFile[]) => {
    setValue("displayJobDocumentFiles", updatedFiles);

    const uploadFiles: TExtendFile[] = updatedFiles.filter((file) => file instanceof File) as TExtendFile[];
    setValue("jobDocumentFiles", uploadFiles);

    const existedPaths: string[] = updatedFiles
      .filter((file) => "filePath" in file && file.filePath)
      .map((file) => (file as { filePath: string }).filePath as string);

    setValue("jobDocumentFilePaths", existedPaths);
  };

  /** Show confirm dialog before DropZone removes the file */
  const confirmBeforeRemove = useCallback(async (removedIndex: number) => {
    const files = methods.getValues("displayJobDocumentFiles") || [];
    pendingDeleteFileName.current = files[removedIndex]?.name || "";
    return confirmDeleteDialog.show();
  }, [methods, confirmDeleteDialog]);

  return (
    <Stack gap={2}>
      <InnerBox title={<JobFormTitle text="応募要件" />} showDivider={false}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormField control={control} name="japaneseLevelType">
              <Select placeholder="選択してください" options={japaneseLevelTypeOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="japaneseLevelOther">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="skillsExperience">
              <Textarea />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="otherApplicationRequirements">
              <Textarea />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="ageLimit">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="nationalityIds" required={!isDraft}>
              <JobSelectMulti placeholder="選択してください" loading={isLoadingNationality} options={nationalityOptionsWithAny} separator="、" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="recruitmentLocationType" required={!isDraft}>
              <Select placeholder="選択してください" options={recruitmentLocationTypeOptions} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="desiredHiringPeriod">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="gender">
              <RadioGroup direction="row" options={JOB_GENDER_OPTIONS} />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="residenceStatusIds" required={!isDraft}>
              <SelectMulti placeholder="選択してください" options={jobResidenceStatusOptions} separator="、" />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="specificSkillCertificateIds">
              <SelectMulti
                value={specificSkillCertificateIdsWatchField ? specificSkillCertificateIdsWatchField.map(String) : []}
                options={companySpecificIndustryIdWatchField ? getCertificatesByIndustryId(companySpecificIndustryIdWatchField) : []}
                placeholder="選択してください"
                disabled={!companySpecificIndustryIdWatchField}
                separator="、"
              />
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="jobDocumentUrl">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="cap12">URL</Typography>
                <Input placeholder="入力してください" fullWidth />
              </Stack>
            </FormField>
          </Grid>
          <Grid item xs={12}>
            <DropZone
              options={{ multiple: true }}
              bordered={true}
              rules={{
                maxFile: MAX_JOB_DOCUMENT_FILE,
                maxSize: MAX_SIZE,
                acceptFileTypesExtension: rules.acceptFileTypesExtension,
                acceptMimeTypes: rules.acceptMimeTypes,
              }}
              enableFileList={false}
              enableFileListCustom={true}
              onChange={handleChangeFile}
              onFileRemove={handleFileRemove}
              confirmBeforeRemove={confirmBeforeRemove}
              value={methods.getValues("displayJobDocumentFiles")}
              noClick={true}
              noKeyboard={true}
              nonControl
              childrenRenderHtml={({ open }) => (
                <>
                  <Stack>
                    <Typography variant="body14Light" color={text.secondary} whiteSpace="pre-wrap" textAlign="center">
                      アップロードするファイルをドラッグ&ドロップ
                    </Typography>
                    <Typography variant="body14Light" color={text.tertiary} whiteSpace="pre-wrap" textAlign="center">
                      （ファイル形式：pdf、xls、xlsx、jpg、jpeg、png、tif、tiffに対応）
                    </Typography>
                  </Stack>
                  <Typography variant="body14Light" color={text.secondary} width={920} textAlign="center">
                    または
                  </Typography>
                  <Button type="button" variant="outline" onClick={open}>
                    ファイルを選択する
                  </Button>
                  <Stack>
                    <Typography
                      variant="cap12"
                      color={text.secondary}
                      whiteSpace="pre-wrap"
                      sx={{
                        display: "list-item",
                        listStyleType: "disc",
                        "::marker": { fontSize: "1em" },
                      }}
                    >
                      容量は最大10MBまでとなります。
                    </Typography>
                  </Stack>
                </>
              )}
            ></DropZone>
          </Grid>
          <Grid item xs={12}>
            <FormField control={control} name="selectionProcess">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </InnerBox>

      <Modal
        isOpen={confirmDeleteDialog.open}
        onClose={confirmDeleteDialog.cancel}
        onOk={confirmDeleteDialog.confirm}
        labelClose="いいえ"
        labelOk="はい"
        width={500}
        title="削除の確認"
      >
        <Stack mb={2}>
          <Typography>「{pendingDeleteFileName.current}」を削除してもよろしいですか？</Typography>
        </Stack>
      </Modal>
    </Stack>
  );
};
